/**
 * ADMIN MODULE — Team service (Brand Command staff management).
 *
 * Manages who has admin access (the platform_admins table) over RLS + the
 * security-definer RPCs from migration 0040. Two tiers:
 *   superadmin → full access AND can manage the team
 *   support    → admin access, cannot manage the team
 *
 * Security model (mirrors the customer invite flow in shared/accountService):
 *   - No service-role edge function. Privileged writes are gated server-side:
 *     inserting a platform_admins row is impossible from the client (no INSERT
 *     policy); promotion happens only via claim-on-login for an invited email.
 *   - Invites are pending rows; only a superadmin can create/revoke them (RLS).
 *   - Role changes / removals go through definer RPCs that re-check superadmin
 *     and refuse to strand the last superadmin or let you remove yourself.
 * Imports only from /shared — no cross-module imports.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

export const ADMIN_ROLES = ["superadmin", "support"];

// Human labels for the two tiers.
export const ADMIN_ROLE_LABELS = {
  superadmin: "Super admin",
  support: "Admin",
};

// The signed-in staffer's own tier ("superadmin" | "support" | null).
// Readable via the existing select-self policy on platform_admins.
export async function myAdminRole() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from("platform_admins")
    .select("role")
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) {
    console.error("[adminTeamService.myAdminRole]", error);
    return null;
  }
  return data?.role ?? null;
}

// Current staff roster, with emails (definer RPC — auth.users isn't readable).
export async function listAdmins() {
  const { data, error } = await supabase.rpc("list_platform_admins");
  if (error) {
    console.error("[adminTeamService.listAdmins]", error);
    throw new Error("Could not load the team.");
  }
  return data ?? [];
}

// Pending staff invites (visible to any admin; revokable by superadmins).
export async function listPendingInvites() {
  const { data, error } = await supabase
    .from("platform_admin_invitations")
    .select("id, email, role, created_at, expires_at")
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[adminTeamService.listPendingInvites]", error);
    throw new Error("Could not load invitations.");
  }
  return data ?? [];
}

export async function inviteAdmin(email, role = "support") {
  const clean = String(email || "").trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(clean)) throw new Error("Enter a valid email address.");
  if (!ADMIN_ROLES.includes(role)) throw new Error("Invalid role.");

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  const { error } = await supabase
    .from("platform_admin_invitations")
    .insert({ email: clean, role, invited_by: user.id });
  if (error) {
    console.error("[adminTeamService.inviteAdmin]", error);
    if (error.code === "23505") throw new Error("There is already an open invite for that email.");
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only super admins can invite staff.");
    }
    throw new Error("Could not create the invite.");
  }
}

export async function revokeInvite(inviteId) {
  const { error } = await supabase
    .from("platform_admin_invitations")
    .update({ status: "revoked" })
    .eq("id", inviteId);
  if (error) {
    console.error("[adminTeamService.revokeInvite]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only super admins can revoke invites.");
    }
    throw new Error("Could not revoke the invite.");
  }
}

export async function setAdminRole(userId, role) {
  if (!ADMIN_ROLES.includes(role)) throw new Error("Invalid role.");
  const { error } = await supabase.rpc("set_platform_admin_role", { target: userId, new_role: role });
  if (error) {
    console.error("[adminTeamService.setAdminRole]", error);
    if (/last superadmin/i.test(error.message ?? "")) {
      throw new Error("You can't demote the only super admin.");
    }
    if (/not authorized/i.test(error.message ?? "")) {
      throw new Error("Only super admins can change roles.");
    }
    throw new Error("Could not change the role.");
  }
}

export async function removeAdmin(userId) {
  const { error } = await supabase.rpc("remove_platform_admin", { target: userId });
  if (error) {
    console.error("[adminTeamService.removeAdmin]", error);
    if (/last superadmin/i.test(error.message ?? "")) {
      throw new Error("You can't remove the only super admin.");
    }
    if (/remove yourself/i.test(error.message ?? "")) {
      throw new Error("You can't remove your own access here.");
    }
    if (/not authorized/i.test(error.message ?? "")) {
      throw new Error("Only super admins can remove staff.");
    }
    throw new Error("Could not remove that person.");
  }
}
